import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { RefreshCw, Search, Filter } from 'lucide-react';
import { adminFetch, fmtNum, fmtRelative } from './api';
import {
  PageHeader, CountCard, RateCard, Section, StatusPill, EmptyState, ErrorBlock,
  SearchInput, Pagination, usePaginatedSearch,
} from './widgets';

const SERIES = [
  { key: 'sent',         label: 'Sent',         color: '#6366F1' },
  { key: 'followup',     label: 'Follow-up',    color: '#A855F7' },
  { key: 'clicks',       label: 'Clicks',       color: '#06B6D4' },
  { key: 'replied',      label: 'Replied',      color: '#10B981' },
  { key: 'bounced',      label: 'Bounced',      color: '#F59E0B' },
  { key: 'unsubscribed', label: 'Unsubscribed', color: '#EF4444' },
];

const EVENT_FILTERS = [
  { key: 'sent',         label: 'Sent',         tone: 'bg-accent/10 text-accent border-accent/30' },
  { key: 'followup',     label: 'Follow-up',    tone: 'bg-violet/10 text-violet border-violet/30' },
  { key: 'click',        label: 'Clicks',       tone: 'bg-accent-2/10 text-accent-2 border-accent-2/30' },
  { key: 'replied',      label: 'Replied',      tone: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  { key: 'bounced',      label: 'Bounced',      tone: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  { key: 'unsubscribed', label: 'Unsubscribed', tone: 'bg-red-500/10 text-red-400 border-red-500/30' },
];

// Per-kind labels for the active-run mini cards on the dashboard.
const RUN_KIND_LABELS = {
  analyze: {
    title: 'Analyze',
    bucket_a: { label: 'found', cls: 'bg-emerald-500' },
    bucket_b: { label: 'no rating', cls: 'bg-amber-500' },
    bucket_c: { label: 'captcha / error', cls: 'bg-red-500' },
  },
  enrich: {
    title: 'Enrich',
    bucket_a: { label: 'found email', cls: 'bg-emerald-500' },
    bucket_b: { label: 'no email', cls: 'bg-amber-500' },
    bucket_c: { label: 'errors', cls: 'bg-red-500' },
  },
};

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [queue, setQueue] = useState(null);
  const [series, setSeries] = useState(null);
  const [days, setDays] = useState(30);
  const [runs, setRuns] = useState(null);
  const [discoverJobs, setDiscoverJobs] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastLoaded, setLastLoaded] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [eventFilter, setEventFilter] = useState(null);
  const [showEventFilters, setShowEventFilters] = useState(false);

  const {
    query: eventsQuery, setQuery: setEventsQuery, debounced: debouncedEvents,
    page: eventsPage, setPage: setEventsPage,
    rows: eventRows, total: eventsTotal, loading: eventsLoading, pageSize: eventsPageSize,
  } = usePaginatedSearch({
    endpoint: '/api/admin-stats/recent-events',
    refreshKey,
    dataKey: 'events',
    extraParams: eventFilter ? { events: eventFilter } : null,
  });

  useEffect(() => { document.title = 'Sudco Admin — Dashboard'; }, []);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [s, q, t, pr, dj] = await Promise.all([
        adminFetch('/api/admin-stats/summary'),
        adminFetch('/api/admin-stats/queue-depth'),
        adminFetch(`/api/admin-stats/timeseries?days=${days}`),
        adminFetch('/api/admin-stats/pipeline-runs?status=running&limit=20'),
        adminFetch('/api/admin-stats/discover-jobs?limit=5'),
      ]);
      setSummary(s); setQueue(q); setSeries(t); setRuns(pr.runs); setDiscoverJobs(dj.jobs);
      setLastLoaded(new Date());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh the dashboard every 5s while a pipeline run is active so
  // the active-runs cards stay live.
  const hasActiveRun = runs && runs.some((r) => r.status === 'running');
  const hasActiveDiscover = discoverJobs && discoverJobs.some((j) => j.status === 'running' || j.status === 'queued');
  useEffect(() => {
    if (!hasActiveRun && !hasActiveDiscover) return undefined;
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [hasActiveRun, hasActiveDiscover, load]);

  const refresh = useCallback(() => { load(); setRefreshKey((k) => k + 1); }, [load]);

  if (error) return <ErrorBlock error={error} onRetry={load} />;
  if (!summary || !queue || !series) {
    return <div className="text-text-muted">Loading…</div>;
  }

  const t = summary.totals;
  const r = summary.rates_30d ?? summary.rates;

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Cold-outreach pipeline at a glance."
        right={
          <div className="flex items-center gap-3">
            {lastLoaded && (
              <span className="text-xs text-text-muted whitespace-nowrap">
                Updated {fmtRelative(lastLoaded.toISOString())}
              </span>
            )}
            <button
              onClick={refresh}
              disabled={loading}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-surface border border-border rounded-lg hover:bg-surface-2 disabled:opacity-60"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        }
      />

      {(hasActiveRun || hasActiveDiscover) && (
        <Section title="Active runs">
          <div className="grid md:grid-cols-2 gap-3">
            {hasActiveDiscover && (
              <DiscoverRunCard
                job={discoverJobs
                  .filter((j) => j.status === 'running' || j.status === 'queued')
                  .reduce((a, b) => (a && a.id > b.id ? a : b), null)}
              />
            )}
            {Object.values(runs.filter((r) => r.status === 'running').reduce((acc, r) => {
              if (!acc[r.kind] || r.id > acc[r.kind].id) acc[r.kind] = r;
              return acc;
            }, {})).map((r) => (
              <ActiveRunCard key={r.id} run={r} />
            ))}
          </div>
          <div className="mt-2 text-xs text-text-muted flex gap-3">
            {hasActiveDiscover && <Link to="/admin/discover" className="hover:text-accent">View discover →</Link>}
            <Link to="/admin/pipeline" className="hover:text-accent">View full pipeline →</Link>
          </div>
        </Section>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CountCard label="Prospects" n={t.prospects} delay={0} />
        <CountCard label="Clicks" n={t.showcase_unique_prospects} sub={`${fmtNum(t.showcase_total)} total clicks`} delay={0.05} />
        <CountCard label="Cold sent" n={t.cold_sent} sub={`${fmtNum(t.followup_sent)} follow-ups`} accent delay={0.1} />
        <CountCard label="Replies" n={t.replied} sub="manually marked" delay={0.15} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
        <RateCard label="Reply rate"  rate={r.reply}       sub={`of ${(r.sent||t.cold_sent).toLocaleString()} sent (30d)`} healthy={(v) => v >= 0.01 ? true : v >= 0.005 ? null : false} delay={0} />
        <RateCard label="Click rate"  rate={r.ctr}         sub="of sent (30d)" healthy={(v) => v >= 0.02 ? true : v >= 0.01 ? null : false} delay={0.05} />
        <RateCard label="Bounce rate" rate={r.bounce}      sub="< 2% healthy · 30d" healthy={(v) => v <= 0.02 ? true : v <= 0.05 ? null : false} delay={0.1} />
        <RateCard label="Unsub rate"  rate={r.unsubscribe} sub="< 0.5% normal · 30d" healthy={(v) => v <= 0.005 ? true : v <= 0.01 ? null : false} delay={0.15} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        <CountCard label="Queue (eligible)" n={queue.eligible} sub={
          queue.days_runway != null
            ? `≈ ${queue.days_runway.toFixed(1)} days at ${queue.daily_capacity}/day`
            : null
        } accent />
        <CountCard label="Bounced" n={t.bounced} delay={0.05} />
        <CountCard label="Unsubscribed" n={t.unsubscribed} delay={0.1} />
      </div>

      <Section
        title="Activity"
        action={
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="bg-surface border border-border rounded-lg px-2 py-1 text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        }
      >
        <div className="rounded-xl border border-border bg-surface p-4 h-80 min-w-0">
          <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 800, height: 320 }}>
            <BarChart data={series.points} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2A3142" />
              <XAxis
                dataKey="day" stroke="#8B92A8" fontSize={11}
                tickFormatter={(d) => d.slice(5)}
                minTickGap={24}
                angle={-35}
                textAnchor="end"
                height={48}
              />
              <YAxis stroke="#8B92A8" fontSize={11} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: '#131826', border: '1px solid #2A3142', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#F5F6FA' }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {SERIES.map((s) => (
                <Bar key={s.key} dataKey={s.key} name={s.label} stackId="a" fill={s.color} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>

      <Section
        title="Recent events"
        action={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowEventFilters((v) => !v)}
              aria-expanded={showEventFilters}
              aria-label="Filter events"
              title="Filter events"
              className={`relative inline-flex items-center justify-center h-8 w-8 rounded-lg border transition-colors ${
                showEventFilters
                  ? 'bg-accent/10 text-accent border-accent/30'
                  : 'bg-surface text-text-muted border-border hover:bg-surface-2 hover:text-text'
              }`}
            >
              <Filter size={14} />
              {eventFilter && (
                <span
                  className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-accent border border-bg"
                  aria-hidden="true"
                />
              )}
            </button>
            <SearchInput value={eventsQuery} onChange={setEventsQuery} placeholder="Search business name…" />
          </div>
        }
      >
        {showEventFilters && (
          <div className="mb-3 flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => setEventFilter(null)}
              aria-pressed={!eventFilter}
              className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                !eventFilter
                  ? 'bg-accent/10 text-accent border-accent/30'
                  : 'bg-surface-2 text-text-muted border-border opacity-60 hover:opacity-90'
              }`}
            >
              All
            </button>
            {EVENT_FILTERS.map((f) => {
              const on = eventFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setEventFilter(f.key)}
                  aria-pressed={on}
                  className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                    on
                      ? f.tone
                      : 'bg-surface-2 text-text-muted border-border opacity-60 hover:opacity-90'
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        )}
        {!eventRows ? (
          <div className="text-text-muted text-sm">Loading…</div>
        ) : eventRows.length === 0 ? (
          <EmptyState
            title={debouncedEvents ? 'No matches' : 'Nothing yet'}
            hint={
              debouncedEvents
                ? `No events match "${debouncedEvents}".`
                : 'Once the agent starts emailing, events will appear here.'
            }
          />
        ) : (
          <>
            <div
              className={`rounded-xl border border-border bg-surface overflow-hidden transition-opacity ${
                eventsLoading ? 'opacity-60' : ''
              }`}
            >
              <div className="overflow-auto max-h-[70vh] divide-y divide-border">
                {eventRows.map((ev, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-2.5 text-sm min-w-0">
                    <StatusPill tone={ev.event}>{ev.event}</StatusPill>
                    <Link
                      to={`/admin/prospects/${ev.prospect_id}`}
                      className="text-text hover:text-accent truncate flex-1 min-w-0"
                    >
                      {ev.business_name || `Prospect #${ev.prospect_id}`}
                    </Link>
                    <span className="text-text-muted text-xs whitespace-nowrap">{fmtRelative(ev.at)}</span>
                  </div>
                ))}
              </div>
            </div>
            <Pagination
              page={eventsPage}
              pageSize={eventsPageSize}
              total={eventsTotal}
              onPage={setEventsPage}
              loading={eventsLoading}
              unitLabel="events"
            />
          </>
        )}
      </Section>
    </>
  );
}

function DiscoverRunCard({ job }) {
  if (!job) return null;
  const skipped = job.skipped_recent || 0;
  const effectiveTotal = Math.max((job.top_n || 0) - skipped, 0);
  const done = job.cities_done || 0;
  const pct = Math.min(100, (done / Math.max(effectiveTotal, 1)) * 100);
  return (
    <div className="rounded-xl border border-border bg-surface p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-sm font-medium text-text flex items-center gap-1.5">
          <Search size={13} className="text-text-muted" />
          Discover
          <span className="text-text-muted font-normal truncate">· {job.query}</span>
        </div>
        <div className="text-xs text-text-muted whitespace-nowrap">
          {fmtNum(done)} / {fmtNum(effectiveTotal)}
          {' · '}{fmtRelative(job.started_at || job.created_at)}
        </div>
      </div>
      <div className="h-2 rounded-full bg-surface-2 overflow-hidden">
        <div className="h-full bg-gradient-brand transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-text-muted">
        <span>{job.region} · cap {fmtNum(job.limit_per_city || 5000)}/city</span>
        <span>{fmtNum(job.prospects_stored_live || 0)} prospects stored</span>
        {skipped > 0 && (
          <span>{fmtNum(skipped)} of {fmtNum(job.top_n)} pre-filtered</span>
        )}
        {job.status === 'queued' && <span className="text-amber-400">queued</span>}
      </div>
    </div>
  );
}

function ActiveRunCard({ run }) {
  const labels = RUN_KIND_LABELS[run.kind] || RUN_KIND_LABELS.analyze;
  const total = Math.max(run.total || 0, 1);
  const pctA = Math.min(100, ((run.bucket_a || 0) / total) * 100);
  const pctB = Math.min(100, ((run.bucket_b || 0) / total) * 100);
  const pctC = Math.min(100, ((run.bucket_c || 0) / total) * 100);
  const done = (run.bucket_a || 0) + (run.bucket_b || 0) + (run.bucket_c || 0);
  return (
    <div className="rounded-xl border border-border bg-surface p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-sm font-medium text-text">{labels.title}</div>
        <div className="text-xs text-text-muted">
          {fmtNum(done)} / {fmtNum(run.total)}
          {' · '}{fmtRelative(run.started_at)}
        </div>
      </div>
      <div className="h-2 rounded-full bg-surface-2 overflow-hidden flex">
        <div className={`h-full transition-all duration-500 ${labels.bucket_a.cls}`} style={{ width: `${pctA}%` }} />
        <div className={`h-full transition-all duration-500 ${labels.bucket_b.cls}`} style={{ width: `${pctB}%` }} />
        <div className={`h-full transition-all duration-500 ${labels.bucket_c.cls}`} style={{ width: `${pctC}%` }} />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-text-muted">
        <span><span className={`inline-block w-2 h-2 rounded-full mr-1 align-middle ${labels.bucket_a.cls}`} />{labels.bucket_a.label} {fmtNum(run.bucket_a || 0)}</span>
        <span><span className={`inline-block w-2 h-2 rounded-full mr-1 align-middle ${labels.bucket_b.cls}`} />{labels.bucket_b.label} {fmtNum(run.bucket_b || 0)}</span>
        <span><span className={`inline-block w-2 h-2 rounded-full mr-1 align-middle ${labels.bucket_c.cls}`} />{labels.bucket_c.label} {fmtNum(run.bucket_c || 0)}</span>
      </div>
    </div>
  );
}
